// @flow
import React, { Fragment } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { Text } from 'Components/frameworks/text/Text.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

export type StopProps = {
  arrivalTime?: string,
  day: string,
  departureTime?: string,
  description?: string,
  title: string,
};

export type ProgramProps = {
  className?: string,
  stops?: Array<StopProps>,
  subtitle?: string,
  title?: string,
};

export type ProductProgramProps = {
  extension?: ProgramProps,
  isDetailed: boolean,
  program: ProgramProps,
};

function ProductProgramSectionTitles({ className, subtitle, title }) {
  return (
    <div className={className}>
      <Title hLevel={2} theme="title-brand-11">
        {title}
      </Title>
      <Title hLevel={3} theme="title-sans-11">
        {subtitle}
      </Title>
    </div>
  );
}

function ProductProgramFull({ className, title, subtitle, stops }: ProgramProps) {
  return isEmpty(stops) ? null : (
    <Fragment>
      <ProductProgramSectionTitles
        className={classnames(className, 'mb-8')}
        subtitle={subtitle}
        title={title}
      />
      <div className="flex flex-wrap -m-3">
        {stops.map(({ day, description, title: stopTitle, departureTime, arrivalTime }) => {
          const departureArrivalTime = [arrivalTime, departureTime].filter(Boolean).join('\n');

          return day || description || stopTitle ? (
            <div className="w-full sm:w-1/2 p-3">
              <Title className="mb-1" hLevel={3} theme="title-sans-3">
                {`${day} - ${stopTitle}`}
              </Title>
              <Text text={description} />
              {!!departureArrivalTime && <Text text={departureArrivalTime} />}
            </div>
          ) : null;
        })}
      </div>
    </Fragment>
  );
}

function ProductProgramLite({ className, title, subtitle, stops }: ProgramProps) {
  return isEmpty(stops) ? null : (
    <section className={classnames(className, 'flex flex-wrap -m-3')}>
      <ProductProgramSectionTitles
        className="w-full sm:w-1/2 p-3"
        subtitle={subtitle}
        title={title}
      />
      <div className="w-full sm:w-1/2 p-3">
        <ul className="reset-list leading-normal text-base text-gray-darker">
          {stops.map(({ day, title: stopTitle }) => {
            return day || stopTitle ? (
              <li className="list-disc py-1" key={day}>
                <strong>{day}</strong> - {stopTitle}
              </li>
            ) : null;
          })}
        </ul>
      </div>
    </section>
  );
}

export function ProductProgram(props: ProductProgramProps) {
  const { extension = {}, isDetailed, program = {} } = props;

  if (isEmpty(program.stops)) return null;

  return isDetailed ? (
    <Fragment>
      <ProductProgramFull stops={program.stops} subtitle={program.subtitle} title={program.title} />
      <ProductProgramFull
        className="mt-10"
        stops={extension.stops}
        subtitle={extension.subtitle}
        title={extension.title}
      />
    </Fragment>
  ) : (
    <Fragment>
      <ProductProgramLite stops={program.stops} subtitle={program.subtitle} title={program.title} />
      <ProductProgramLite
        className="mt-10"
        stops={extension.stops}
        subtitle={extension.subtitle}
        title={extension.title}
      />
    </Fragment>
  );
}
